import { useCustomQuery } from "@/hooks/useCustomQuery"
import { Box, Typography, Grid } from "@mui/material"
import { useParams, Link } from "react-router-dom"
import Loading from "@/components/Loading"
import DataTable from "@/components/Tables/DataTable"
import dayjs from "dayjs"

const CashDenominationDetails = () => {
  const { id } = useParams()

  const { data: response, isLoading } = useCustomQuery(
    ["cash", id],
    `cash/${id}`
  )

  if (isLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  const session = response?.data

  const denominations = session?.denominations ?? []

  const openingDenominations = denominations.filter(
    (d) => d.stage === "opening"
  )

  const closingDenominations = denominations.filter(
    (d) => d.stage === "closing"
  )

  const denominationColumns = [
    { header: "Currency", accessorKey: "currency_code" },
    { header: "Denomination", accessorKey: "denomination" },
    { header: "Quantity", accessorKey: "quantity" },
    { header: "Rate (NPR)", accessorKey: "exchange_rate_to_npr" },
    { header: "Amount", accessorKey: "amount_in_currency" },
    { header: "Amount (NPR)", accessorKey: "amount_in_npr" },
  ]

  return (
    <div>
      {/* Breadcrumb */}
      <div className="breadcrumb-card">
        <h5>Cash Session Details</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/pos/dashboard">Dashboard</Link>
          </li>
          <li>
            <Link to="/pos/cash-denominations">Cash Sessions</Link>
          </li>
          <li>Session #{session?.id}</li>
        </ul>
      </div>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Box className="card p-4">
            <Typography variant="body2">Opening Total (NPR)</Typography>
            <Typography variant="h6">{session?.opening_total_npr}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box className="card p-4">
            <Typography variant="body2">Expected Total (NPR)</Typography>
            <Typography variant="h6">{session?.expected_total_npr}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box className="card p-4">
            <Typography variant="body2">Closing Total (NPR)</Typography>
            <Typography variant="h6">{session?.closing_total_npr}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box className="card p-4">
            <Typography variant="body2">Difference (NPR)</Typography>
            <Typography
              variant="h6"
              color={Number(session?.difference_npr) >= 0 ? "error" : "success"}
            >
              {session?.difference_npr}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Session Meta */}
      <Box className="card p-4 mb-4">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2">Status</Typography>
            <Typography fontWeight={600}>
              {session?.status?.toUpperCase()}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body2">Opened At</Typography>
            <Typography>
              {dayjs(session?.opened_at).format("YYYY-MM-DD HH:mm:ss")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body2">Closed At</Typography>
            <Typography>
              {dayjs(session?.closed_at).format("YYYY-MM-DD HH:mm:ss")}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Opening Denominations */}
      <Box className="card p-4 mb-4">
        <Typography variant="h6" mb={2}>
          Opening Denominations
        </Typography>

        <DataTable
          columns={denominationColumns}
          data={openingDenominations}
          isLoading={isLoading}
        />
      </Box>

      {/* Closing Denominations */}
      <Box className="card p-4">
        <Typography variant="h6" mb={2}>
          Closing Denominations
        </Typography>

        <DataTable
          columns={denominationColumns}
          data={closingDenominations}
          isLoading={isLoading}
        />
      </Box>
    </div>
  )
}

export default CashDenominationDetails
